import { useCallback, useRef, useState } from 'react';
import { useNotify } from '@/hooks/useNotify';
import { useAppSelector } from '@/store/hooks';
import type { LocationImportResult } from '@/types/location';
import { resolveMutationResult } from '@/utils/apiResponse';
import { downloadAuthenticatedFile } from '@/utils/downloadFile';
import { LocationBulkUploadDropdown } from './LocationBulkUploadDropdown';
import { LocationImportResultModal } from './LocationImportResultModal';

interface UseLocationBulkUploadOptions {
  entityLabel: string;
  entityLabelPlural: string;
  exportPath: string;
  templatePath: string;
  exportFilename: string;
  templateFilename: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importMutation: (file: File) => any;
  isImporting: boolean;
  onImported?: () => void;
}

export function useLocationBulkUpload({
  entityLabel,
  entityLabelPlural,
  exportPath,
  templatePath,
  exportFilename,
  templateFilename,
  importMutation,
  isImporting,
  onImported,
}: UseLocationBulkUploadOptions) {
  const notify = useNotify();
  const accessToken = useAppSelector((store) => store.auth.accessToken);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importResult, setImportResult] = useState<LocationImportResult | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    try {
      const filename = await downloadAuthenticatedFile(
        templatePath,
        accessToken,
        templateFilename,
      );
      notify.success(`Downloaded ${filename}`);
    } catch {
      notify.error(`Could not download the ${entityLabel.toLowerCase()} template.`);
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [accessToken, entityLabel, notify, templateFilename, templatePath]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const filename = await downloadAuthenticatedFile(exportPath, accessToken, exportFilename);
      notify.success(`Exported ${filename}`);
    } catch {
      notify.error(`Could not export ${entityLabelPlural.toLowerCase()}.`);
    } finally {
      setIsExporting(false);
    }
  }, [accessToken, entityLabelPlural, exportFilename, exportPath, notify]);

  const handleImportFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      const result = await resolveMutationResult<LocationImportResult>(importMutation(file));
      if (!result.ok) {
        notify.error(result.message);
        return;
      }

      setImportResult(result.data);
      setImportMessage(result.message ?? null);
      setImportModalOpen(true);
      notify.success(result.message ?? `${entityLabel} import finished.`);
      onImported?.();
    },
    [entityLabel, importMutation, notify, onImported],
  );

  const bulkUploadControls = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(event) => void handleImportFile(event)}
      />
      <LocationBulkUploadDropdown
        entityLabel={entityLabel}
        entityLabelPlural={entityLabelPlural}
        onDownloadTemplate={() => void handleDownloadTemplate()}
        onExport={() => void handleExport()}
        onImport={() => fileInputRef.current?.click()}
        isDownloadingTemplate={isDownloadingTemplate}
        isExporting={isExporting}
        isImporting={isImporting}
      />
      <LocationImportResultModal
        open={importModalOpen}
        result={importResult}
        message={importMessage}
        onClose={() => setImportModalOpen(false)}
      />
    </>
  );

  return { bulkUploadControls };
}
